import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { api } from "../api";
import { when } from "../accounts";
import { errorText, unwrap } from "../problem";
import {
  actorLabel,
  boundsLabel,
  draftFor,
  draftValue,
  valueLabel,
  type Setting,
  type SettingDraft,
} from "../settings";

// Instance settings apply on the server's next check, without a restart.
// Every write is recorded in the audit log (`setting.set`), which is also the
// change history below.
export function SettingsPage() {
  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: async () => unwrap(await api.GET("/settings")),
  });
  return (
    <>
      <h1>Settings</h1>
      <p className="muted">
        Instance settings take effect at once, without a restart. Every change is recorded in the audit log.
      </p>
      {settings.isPending && <p>Loading…</p>}
      {settings.isError && <p className="error">{errorText(settings.error)}</p>}
      {settings.data && (
        <table>
          <thead>
            <tr>
              <th>Setting</th>
              <th>Value</th>
              <th>Default</th>
              <th>Last changed</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {settings.data.settings.map((setting) => (
              <SettingRow key={setting.key} setting={setting} />
            ))}
          </tbody>
        </table>
      )}
      {settings.data && <History settings={settings.data.settings} />}
    </>
  );
}

function SettingRow({ setting }: { setting: Setting }) {
  const [editing, setEditing] = useState(false);
  return (
    <>
      <tr>
        <td>
          <code>{setting.key}</code>
          <div className="muted">{setting.description}</div>
        </td>
        <td>
          <strong>{valueLabel(setting, setting.value)}</strong>
        </td>
        <td>{valueLabel(setting, setting.default)}</td>
        <td>
          {setting.updated_at ? (
            <>
              {when(setting.updated_at)}
              <div className="muted">{actorLabel(setting.updated_by)}</div>
            </>
          ) : (
            <span className="muted">Never (default)</span>
          )}
        </td>
        <td>
          {!editing && (
            <button type="button" className="button-quiet" onClick={() => setEditing(true)}>
              Change…
            </button>
          )}
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={5}>
            <SettingEditor setting={setting} onClose={() => setEditing(false)} />
          </td>
        </tr>
      )}
    </>
  );
}

function SettingEditor({ setting, onClose }: { setting: Setting; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SettingDraft>(() => draftFor(setting, setting.value));
  const [invalid, setInvalid] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (value: Setting["value"]) =>
      unwrap(await api.PUT("/settings/{key}", { params: { path: { key: setting.key } }, body: { value } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
      void queryClient.invalidateQueries({ queryKey: ["setting-history"] });
      onClose();
    },
  });
  const bounds = boundsLabel(setting);

  function submit(event: FormEvent) {
    event.preventDefault();
    const result = draftValue(setting, draft);
    if ("error" in result) {
      setInvalid(result.error);
      return;
    }
    setInvalid(null);
    const text = `Set ${setting.key} to ${valueLabel(setting, result.value)}? It applies at once.`;
    if (window.confirm(text)) save.mutate(result.value);
  }

  return (
    <form className="filters" onSubmit={submit}>
      {setting.type === "boolean" ? (
        <label>
          Value
          <select
            value={draft.on ? "on" : "off"}
            onChange={(event) => setDraft({ ...draft, on: event.target.value === "on" })}
          >
            <option value="on">On</option>
            <option value="off">Off</option>
          </select>
        </label>
      ) : (
        <label>
          Value{bounds ? ` (${bounds})` : ""}
          <input
            type="number"
            inputMode="numeric"
            step={1}
            min={setting.minimum ?? undefined}
            max={setting.maximum ?? undefined}
            value={draft.text}
            disabled={setting.nullable && draft.none}
            onChange={(event) => setDraft({ ...draft, text: event.target.value })}
          />
        </label>
      )}
      {setting.nullable && (
        <label className="inline">
          <input
            type="checkbox"
            checked={draft.none}
            onChange={(event) => setDraft({ ...draft, none: event.target.checked })}
          />
          {setting.none_label ?? "None"}
        </label>
      )}
      <button type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving…" : "Save"}
      </button>
      <button
        type="button"
        className="button-quiet"
        onClick={() => setDraft(draftFor(setting, setting.default))}
        disabled={save.isPending}
      >
        Default ({valueLabel(setting, setting.default)})
      </button>
      <button type="button" className="button-quiet" onClick={onClose} disabled={save.isPending}>
        Cancel
      </button>
      {(invalid ?? save.error) && <p className="error">{invalid ?? errorText(save.error)}</p>}
    </form>
  );
}

function History({ settings }: { settings: Setting[] }) {
  const [key, setKey] = useState("");
  const history = useInfiniteQuery({
    queryKey: ["setting-history", key],
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/settings/history", {
          params: { query: { ...(key ? { key } : {}), ...(pageParam ? { cursor: pageParam } : {}) } },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
  });
  const byKey = new Map(settings.map((setting) => [setting.key, setting]));
  const entries = history.data?.pages.flatMap((page) => page.entries) ?? [];
  const label = (settingKey: string, value: Setting["value"]) => {
    const setting = byKey.get(settingKey);
    return setting ? valueLabel(setting, value) : String(value);
  };
  return (
    <section className="stack history">
      <div className="section-head">
        <h2>Change history</h2>
        <label className="inline">
          Setting
          <select value={key} onChange={(event) => setKey(event.target.value)}>
            <option value="">All settings</option>
            {settings.map((setting) => (
              <option key={setting.key} value={setting.key}>
                {setting.key}
              </option>
            ))}
          </select>
        </label>
      </div>
      {history.isPending && <p>Loading…</p>}
      {history.isError && <p className="error">{errorText(history.error)}</p>}
      {history.data && entries.length === 0 && <p className="muted">No changes recorded yet.</p>}
      {entries.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Setting</th>
              <th>From</th>
              <th>To</th>
              <th>By</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td>{when(entry.at)}</td>
                <td>
                  <code>{entry.key}</code>
                </td>
                <td>
                  {label(entry.key, entry.previous)}
                  {entry.previous_default && <span className="muted"> (default)</span>}
                </td>
                <td>{label(entry.key, entry.value)}</td>
                <td>{actorLabel(entry.by)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {history.hasNextPage && (
        <button
          type="button"
          className="button-quiet"
          onClick={() => void history.fetchNextPage()}
          disabled={history.isFetchingNextPage}
        >
          {history.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </section>
  );
}
